import { ColumnDef } from "@tanstack/react-table";
import { certificationStatus, ObjectionLevelTitles } from "../../../constant";
import { ReactComponent as StarIcon } from "../../../assets/icons/star.svg";
import { DataTable } from "../../certifications/components/data-table";
import { secondsToMinute } from "../../praxis-call/utils";
import { useReports } from "../context/reports-context";
import { useApp } from "../../../services/app-context";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getPersonalityLabels, getPractitionerTypeMap } from "../../../services/utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";
import React, { useMemo, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

dayjs.extend(utc);
dayjs.extend(timezone);


const filterData = (data: any[], filter: any) => {
  let filteredData = [...data];
  if (filter.search) {
    filteredData = filteredData.filter((item) => {
      const searchTerm = filter.search.toLowerCase();
      const certName =
        item?.practitionerType?.toLowerCase() ?? "";
      return certName.includes(searchTerm);
    });
  }
  if (filter.sortBy && filter.sortOrder) {
    filteredData = filteredData.sort((a, b) => {
      if (filter.sortBy === "createdAt") {
        // date filter
        const aDate = dayjs(a.createdAt).utc().format("YYYY-MM-DD");
        const bDate = dayjs(b.createdAt).utc().format("YYYY-MM-DD");
        const before =
          filter.sortOrder === "desc"
            ? dayjs(bDate).isBefore(aDate)
            : dayjs(aDate).isBefore(bDate);
        return before ? -1 : 1;
      }
      return filter.sortOrder === "desc"
        ? b[filter.sortBy] - a[filter.sortBy]
        : a[filter.sortBy] - b[filter.sortBy];
    });
  }
  return filteredData;
};

export function StatusCell({ status }: { status: string }) {
  const statusData =
    certificationStatus[status as keyof typeof certificationStatus] ||
    certificationStatus.NotStarted;
  return (
    <div
      className={`rounded-md w-[102px] h-[37px] flex items-center justify-center text-[13px]`}
      style={{
        backgroundColor: statusData?.bgColor,
        color: statusData?.textColor,
      }}
    >
      {statusData?.label}
    </div>
  );
}

export function PraxisScoreCell({ score, className, showStar = true }: { score: number, className?: string, showStar?: boolean }) {
  const hasScore = score || score === 0;
  const status = !hasScore ? "N/A" : score < 3 ? "Fail" : "Pass";
  const statusData = certificationStatus[
    status as keyof typeof certificationStatus
  ] || {
    label: "N/A",
    bgColor: "transparent",
    textColor: "#C6C6C6",
  };
  return (
    <div
      className={twMerge(`rounded-md gap-2 w-[102px] h-[37px] flex items-center justify-center text-[13px]`, className)}
      style={{
        backgroundColor: statusData.bgColor,
        color: statusData.textColor,
      }}
    >
      {hasScore && showStar && <StarIcon className="w-4 h-4" />}
      {hasScore ? score : "N/A"}
    </div>
  );
}

const getColumns = (
  { tenantProfile }: { tenantProfile: any }
) => {
  const practitionerTypeList = getPractitionerTypeMap(tenantProfile);
  const commonColumns = [
    {
      accessorKey: "personalityType",
      header: "Personality Type",
      cell: ({ row }) => {
        return <div>{getPersonalityLabels(row.original.personalityType, tenantProfile)}</div>;
      },
    },
    {
      accessorKey: "objectionLevel",
      header: ({ column }) => {
        return <div className="p-0">Objection</div>;
      },
      cell: ({ row }) => {
        return (
          <div>
            {ObjectionLevelTitles[
              row.original.objectionLevel as keyof typeof ObjectionLevelTitles
            ]}
          </div>
        );
      },
    },
    {
      accessorKey: "duration",
      header: "Call time",
      cell: ({ row }) => {
        return `${secondsToMinute(row.original.durationSeconds || 0)} mins`;
      },
    },
    {
      accessorKey: "finalScore",
      header: "Praxis Score",
      size: 150,
      cell: ({ row }) => {
        const hasEndReason = !!row.original?.endReason || !!row.original?.end_reason;
        const isPracticeMode = row.original?.isUnscored;
        if (isPracticeMode) {
          return (
            <div className="w-[140px] h-[37px] flex items-center justify-center text-text-gray-400 rounded-md text-xs bg-gray-301">
              Practice Mode
            </div>
          );
        }
        if (hasEndReason) {
          return (
            <div className="w-[140px] h-[37px] flex items-center justify-center rounded-md text-[13px] text-gray-500 bg-transparent">
              Call Ended
            </div>
          );
        }
        return (
          <PraxisScoreCell
            score={row.original?.score || 0}
            className="!w-[140px]"
          />
        );
      },
    },
  ];
  return [
    {
      accessorKey: "Date Created",
      header: ({ column }) => {
        return <div className="p-0">Date</div>;
      },
      cell: ({ row }) => {
        return (
          <span>
            {dayjs(row.original.startTime)
              .utc()
              .format("MMM DD, YYYY")}
          </span>
        );
      },
    },
    {
      accessorKey: "practitionerType",
      header: ({ column }) => {
        return <div className="p-0">AI HCP Type</div>;
      },
      cell: ({ row }) => {
        return <div>{practitionerTypeList[row.original.practitionerType] || row.original.practitionerType}</div>;
      },
    },
    ...commonColumns,
  ];
};

type User = {
  id: string;
  name: string;
  email: string;
  joinedOn: string;
  tier: string;
  totalSellers: number;
  status: string;
  statusText: string;
  statusDescription: string;
  totalSpend: string;
  stripeCustomerId: string;
  subscription: any;
  openDrawer: (row: any) => void;
  deleteModal: (row: any) => void;
  deactivateModal: (row: any) => void;
};

function ReportsTableInner({
  filter,
  onRowClick,
  enablePagination = false,
}: {
  filter?: any;
  onRowClick: (row: any) => void;
  enablePagination?: boolean;
}) {
  const { userReports: previewReports, isLoadingReports: isLoadingPreview } = useReports();
  const { tenantProfile } = useApp();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Infinite query for paginated data
  const {
    data: paginatedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPaginated,
  } = useInfiniteQuery({
    queryKey: ["paginated-reports-table"],
    queryFn: ({ pageParam }) => api.getAllReports(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage?.data?.links?.after || undefined;
    },
    enabled: enablePagination,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Combine all pages of paginated data
  const allReports = useMemo(() => {
    if (enablePagination && paginatedData?.pages) {
      return paginatedData.pages.flatMap((page) =>
        (page?.data?.data || [])
      );
    }
    return [];
  }, [enablePagination, paginatedData]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!enablePagination || !sentinelRef.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // When sentinel becomes visible and we have more data
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current, // Use the scrollable container as root
        rootMargin: '200px', // Start loading 200px before sentinel is visible
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [enablePagination, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Use paginated data if enabled, otherwise use preview data from context
  const reports = enablePagination ? allReports : previewReports;

  const isLoading = enablePagination ? isLoadingPaginated : isLoadingPreview;

  const filteredData = useMemo(
    () => filterData(reports || [], filter ?? {}),
    [reports, filter?.search, filter?.sortBy, filter?.sortOrder]
  );

  const columns: ColumnDef<User>[] = useMemo(
    () =>
      getColumns({
        tenantProfile,
      }),
    [tenantProfile]
  );

  return (
    <div className="w-full flex flex-col gap-5">
      <DataTable
        columns={columns}
        onRowClick={onRowClick}
        data={filteredData}
        isLoading={isLoading}
        isRowClickable={(row) => !row?.endReason && !row?.end_reason}
        scrollContainerRef={scrollContainerRef}
        footerContent={
          enablePagination ? (
            <>
              {/* Sentinel element for infinite scroll */}
              {hasNextPage && (
                <div ref={sentinelRef} className="h-px w-full" />
              )}

              {isFetchingNextPage && (
                <div className="text-center py-2 bg-white">
                  <span className="text-text-gray text-sm">Loading more...</span>
                </div>
              )}
            </>
          ) : null
        }
      />
    </div>
  );
}

export const ReportsTable = React.memo(ReportsTableInner);
